// ---------------------------------------------------------------------------
// DESCRIÇÃO RICA DO EMPREENDIMENTO — sanitização + extração de FAQ.
//
// O texto vem do CRM (coluna legada `DescricaoEmpre`, exposta pela query
// pública como `descricao`) e HOJE é injetado cru com dangerouslySetInnerHTML.
// Este módulo é o choke point: allowlist de tags, TODOS os atributos zerados
// (só `href` de <a> sobrevive, com esquema validado) e nenhum <script>/<iframe>.
//
// O parser de FAQ é PURO (sem DOM): roda igual no SSR e no cliente, e alimenta
// o JSON-LD FAQPage — o que faz a página ser citada por IA quando alguém
// pergunta "o <Condomínio> tem piscina?".
// ---------------------------------------------------------------------------

// Tags de conteúdo que o redator usa no CRM. Qualquer outra é DESCARTADA
// preservando o texto interno (não se perde conteúdo, só marcação).
const TAGS_PERMITIDAS = new Set([
    'p', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'strong', 'em', 'b', 'i', 'a', 'br',
]);

// Elementos removidos COM o conteúdo (não faz sentido preservar o texto de um
// <script> ou de um <style>).
const TAGS_REMOVIDAS = 'script|style|iframe|object|embed|form|svg|math|noscript|template';

const escapaAtributo = (valor: string): string =>
    valor.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// href seguro: http(s), mailto:, tel:, âncora ou caminho relativo do próprio
// site. `javascript:` e `data:` são recusados.
const hrefSeguro = (attrs: string): string => {
    const m = /href\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(String(attrs || ''));
    const bruto = String(m?.[1] ?? m?.[2] ?? m?.[3] ?? '').trim();
    if (!bruto) return '';
    // Tira caracteres de CONTROLE, que é justamente como se mascara o esquema
    // ("java\nscript:" passa por qualquer teste ingênuo). O eslint-disable é
    // proposital: a regra existe pra pegar control char acidental em regex, e
    // aqui ele é o alvo.
    // eslint-disable-next-line no-control-regex
    const limpo = bruto.replace(/[\x00-\x1F\x7F]/g, '');
    if (/^(https?:|mailto:|tel:)/i.test(limpo)) return escapaAtributo(limpo);
    if (/^[/#]/.test(limpo)) return escapaAtributo(limpo);
    return '';
};

export const sanitizeDescricaoHtml = (raw: string | null | undefined): string => {
    let html = String(raw || '');
    if (!html.trim()) return '';

    html = html.replace(new RegExp(`<(${TAGS_REMOVIDAS})\\b[\\s\\S]*?<\\/\\1\\s*>`, 'gi'), '');
    html = html.replace(new RegExp(`<\\/?(${TAGS_REMOVIDAS})\\b[^>]*>`, 'gi'), '');
    html = html.replace(/<!--[\s\S]*?-->/g, '');

    html = html.replace(/<(\/?)([a-zA-Z][a-zA-Z0-9]*)\b([^>]*)>/g, (_todo, barra, tag, attrs) => {
        const nome = String(tag).toLowerCase();
        if (!TAGS_PERMITIDAS.has(nome)) return ''; // descarta a MARCAÇÃO, mantém o texto
        if (barra) return `</${nome}>`;
        if (nome === 'br') return '<br />';
        if (nome === 'a') {
            const href = hrefSeguro(attrs);
            // link do redator sai com rel de segurança; sem href válido vira <a> inerte
            return href ? `<a href="${href}" rel="noopener noreferrer">` : '<a>';
        }
        return `<${nome}>`; // zera 100% dos atributos (mata on*, style, class)
    });

    return html.trim();
};

export const stripTags = (raw: string | null | undefined): string =>
    String(raw || '')
        .replace(new RegExp(`<(${TAGS_REMOVIDAS})\\b[\\s\\S]*?<\\/\\1\\s*>`, 'gi'), ' ')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;|&apos;/gi, "'")
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/\s+/g, ' ')
        .trim();

/** Corta no limite sem quebrar palavra, com reticências. */
export const clampTexto = (texto: string | null | undefined, max: number): string => {
    const limpo = String(texto || '').trim();
    if (!limpo || limpo.length <= max) return limpo;
    const cortado = limpo.slice(0, max - 1);
    const espaco = cortado.lastIndexOf(' ');
    return `${(espaco > max * 0.6 ? cortado.slice(0, espaco) : cortado).trim()}…`;
};

// Frase curta demais quase sempre é abreviação ("Ed.", "Ap. 302", "Sr.") —
// abaixo disto o ponto NÃO encerra a frase.
const MIN_FRASE = 20;

/** 1ª frase do texto (meta description quando não há uma cadastrada). */
export const primeiraFrase = (raw: string | null | undefined, max = 155): string => {
    const texto = stripTags(raw);
    if (!texto) return '';
    const fim = /[.!?](\s|$)/g;
    let achado: RegExpExecArray | null;
    while ((achado = fim.exec(texto)) !== null) {
        if (achado.index >= MIN_FRASE) return clampTexto(texto.slice(0, achado.index + 1), max);
    }
    return clampTexto(texto, max);
};

// ------------------------------- FAQ --------------------------------------

export type ParFaq = { pergunta: string; resposta: string };

// Cabeçalho que abre a seção de perguntas. Aceita as variações que o redator
// costuma usar; o acento é opcional porque o CRM às vezes salva sem.
const TITULO_FAQ = /perguntas?\s+frequentes|d[uú]vidas?\s+frequentes|^faq$/i;
const MAX_PARES_FAQ = 20;
const MAX_PERGUNTA = 300;

/**
 * Extrai os pares `<h3>Pergunta?</h3><p>Resposta.</p>` que vêm DEPOIS de um
 * `<h2>Perguntas frequentes</h2>`, até o próximo `<h2>` (ou o fim do texto).
 * Sem a seção, ou sem par completo, devolve `[]` — e aí nenhum FAQPage é
 * emitido (FAQ vazio no JSON-LD é penalizado pelo Google).
 */
export const extraiFaqDaDescricao = (raw: string | null | undefined): ParFaq[] => {
    const html = String(raw || '');
    if (!html.trim()) return [];

    // 1) acha o <h2> da seção de perguntas
    const h2 = /<h2[^>]*>([\s\S]*?)<\/h2>/gi;
    let inicio = -1;
    let achado: RegExpExecArray | null;
    while ((achado = h2.exec(html)) !== null) {
        if (TITULO_FAQ.test(stripTags(achado[1]))) {
            inicio = achado.index + achado[0].length;
            break;
        }
    }
    if (inicio < 0) return [];

    // 2) recorta até o próximo <h2> (a FAQ não engole as seções seguintes)
    const resto = html.slice(inicio);
    const proximoH2 = /<h2[^>]*>/i.exec(resto);
    const bloco = proximoH2 ? resto.slice(0, proximoH2.index) : resto;

    // 3) cada <h3> abre um par; a resposta é tudo até o próximo <h3>
    const pares: ParFaq[] = [];
    const h3 = /<h3[^>]*>([\s\S]*?)<\/h3>([\s\S]*?)(?=<h3[^>]*>|$)/gi;
    let par: RegExpExecArray | null;
    while ((par = h3.exec(bloco)) !== null && pares.length < MAX_PARES_FAQ) {
        const pergunta = stripTags(par[1]);
        const resposta = stripTags(par[2]);
        if (!pergunta || !resposta) continue;
        if (pergunta.length > MAX_PERGUNTA) continue; // não é pergunta, é parágrafo
        pares.push({ pergunta, resposta });
    }
    return pares;
};
